import React, { useEffect, useState } from "react";
import { Modal, FormActions, Input } from "../../shared";
import { appointmentsApi } from "../../../services/appointmentsApi";

interface AppointmentNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: number;
  initialNotes?: string | null;
  initialDeliveryStartTime?: string | null;
  initialDeliveryEndTime?: string | null;
  showDeliveryFields?: boolean;
  onSuccess: () => void;
}

const DEFAULT_DELIVERY_START_TIME = "08:00";
const DEFAULT_DELIVERY_END_TIME = "17:00";

function toTimeInputValue(value?: string | null): string {
  if (!value) return "";
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

export const AppointmentNotesModal: React.FC<AppointmentNotesModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  initialNotes,
  initialDeliveryStartTime,
  initialDeliveryEndTime,
  showDeliveryFields = false,
  onSuccess,
}) => {
  const hasSavedDeliveryTimes = Boolean(
    initialDeliveryStartTime || initialDeliveryEndTime
  );
  const [notes, setNotes] = useState(initialNotes || "");
  const [deliveryTimesEnabled, setDeliveryTimesEnabled] = useState(
    hasSavedDeliveryTimes
  );
  const [deliveryStartTime, setDeliveryStartTime] = useState(
    toTimeInputValue(initialDeliveryStartTime)
  );
  const [deliveryEndTime, setDeliveryEndTime] = useState(
    toTimeInputValue(initialDeliveryEndTime)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const savedTimes = Boolean(
        initialDeliveryStartTime || initialDeliveryEndTime
      );
      setNotes(initialNotes || "");
      setDeliveryTimesEnabled(savedTimes);
      setDeliveryStartTime(toTimeInputValue(initialDeliveryStartTime));
      setDeliveryEndTime(toTimeInputValue(initialDeliveryEndTime));
      setError(null);
    }
  }, [
    isOpen,
    initialNotes,
    initialDeliveryStartTime,
    initialDeliveryEndTime,
  ]);

  const handleDeliveryTimesToggle = () => {
    setDeliveryTimesEnabled((prev) => {
      const next = !prev;
      if (next) {
        setDeliveryStartTime(
          (current) => current || DEFAULT_DELIVERY_START_TIME
        );
        setDeliveryEndTime((current) => current || DEFAULT_DELIVERY_END_TIME);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (
      showDeliveryFields &&
      deliveryTimesEnabled &&
      deliveryStartTime &&
      deliveryEndTime &&
      deliveryEndTime <= deliveryStartTime
    ) {
      setError("O horário final de entrega deve ser após o horário inicial");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: {
        notes: string | null;
        deliveryStartTime?: string | null;
        deliveryEndTime?: string | null;
      } = {
        notes: notes.trim() || null,
      };

      if (showDeliveryFields && deliveryTimesEnabled) {
        payload.deliveryStartTime = deliveryStartTime.trim() || null;
        payload.deliveryEndTime = deliveryEndTime.trim() || null;
      }

      const response = await appointmentsApi.updateAppointment(
        appointmentId,
        payload
      );

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.error || "Falha ao salvar observações");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha ao salvar observações"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Observações do Agendamento" size="sm">
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Observações
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adicione observações sobre este agendamento..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors duration-200 resize-none"
          />
        </div>

        {showDeliveryFields && (
          <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/80 p-3 min-w-0 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  Horário de entrega
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {deliveryTimesEnabled
                    ? "Defina o intervalo de entrega"
                    : "Ative para definir o horário de entrega"}
                </p>
              </div>
              <button
                type="button"
                onClick={handleDeliveryTimesToggle}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 self-start sm:self-auto items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
                  deliveryTimesEnabled ? "bg-sky-600" : "bg-gray-300"
                }`}
                title={
                  deliveryTimesEnabled
                    ? "Desativar horário de entrega"
                    : "Ativar horário de entrega"
                }
                aria-pressed={deliveryTimesEnabled}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                    deliveryTimesEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 min-w-0">
              <Input
                label="Entrega — início"
                type="time"
                value={deliveryStartTime}
                onChange={(e) => setDeliveryStartTime(e.target.value)}
                disabled={!deliveryTimesEnabled}
                className="touch-manipulation"
              />
              <Input
                label="Entrega — fim"
                type="time"
                value={deliveryEndTime}
                onChange={(e) => setDeliveryEndTime(e.target.value)}
                disabled={!deliveryTimesEnabled}
                className="touch-manipulation"
              />
            </div>
          </div>
        )}

        <FormActions
          onCancel={onClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={Boolean(
            initialNotes ||
              (deliveryTimesEnabled &&
                (initialDeliveryStartTime || initialDeliveryEndTime))
          )}
          submitText="Salvar"
          submitType="button"
        />
      </div>
    </Modal>
  );
};
